import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Container,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Box,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Divider,
} from "@mui/material";
import { Delete, Add, Save, LibraryAddCheck } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setConceptos } from "../redux/actions";
import  { apiUrl } from '../apiConfig';
import PlantillaConceptosDialog from "../components/PlantillaConceptosDialog";
import { ensureDefaultConceptos } from "../data/conceptosCapitalizacion";

function ConceptosCatalogo() {
  const [records, setRecords] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [plantillaOpen, setPlantillaOpen] = useState(false);
  const hasFetchedRef = useRef(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const conceptosFromStore = useSelector((state) => state.conceptos);

  const agrupaciones = [
    "Venta", "Abandono / deserción", "Costo (COGS)", "Gasto Administrativo",
    "Gasto operativo", "Gasto de venta", "Depreciación", "Amortización",
    "Intereses bancarios", "Intereses por préstamo", "Comisiones bancarias",
    "Otras Comisiones",
    "Impuesto sobre la renta (ISR)", "Impuesto para el trabajador (PTU)",
    "Impuesto sobre nómina (ISN)", "Gasto personal", "Actividades de operación",
    "Actividades de inversión", "Actividades de financiamiento",
  ];

  const agrupacionesMapeadas = {
    "Venta": "Estado de Resultados",
    "Abandono / deserción": "Estado de Resultados",
    "Costo (COGS)": "Estado de Resultados",
    "Gasto Administrativo": "Estado de Resultados",
    "Gasto operativo": "Estado de Resultados",
    "Gasto de venta": "Estado de Resultados",
    "Depreciación": "Ambos",
    "Amortización": "Ambos",
    "Intereses bancarios": "Estado de Resultados",
    "Intereses por préstamo": "Estado de Resultados",
    "Comisiones bancarias": "Estado de Resultados",
    "Otras Comisiones": "Estado de Resultados",
    "Impuesto sobre la renta (ISR)": "Estado de Resultados",
    "Impuesto para el trabajador (PTU)": "Estado de Resultados",
    "Impuesto sobre nómina (ISN)": "Estado de Resultados",
    "Gasto personal": "Flujo de Efectivo",
    "Actividades de operación": "Flujo de Efectivo",
    "Actividades de inversión": "Flujo de Efectivo",
    "Actividades de financiamiento": "Flujo de Efectivo"
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchData = async () => {
      setIsLoading(true);
      const token = sessionStorage.getItem("token");

      try {
        const response = await fetch(apiUrl("api/v1/catalogo/conceptos"), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.status === 401 || response.status === 403) {
          sessionStorage.removeItem("token");
          navigate("/login");
          return;
        }

        if (!response.ok) {
          throw new Error("Failed to fetch conceptos");
        }

        const data = await response.json();
        // Los conceptos de "Capitalizaciones a cuentas propias" siempre deben
        // estar presentes por defecto en el catálogo.
        const conceptos = ensureDefaultConceptos(data.conceptos);
        dispatch(setConceptos(conceptos));
        setRecords(conceptos);
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Error al cargar los datos. Intente recargar la página.");
        setRecords(ensureDefaultConceptos(conceptosFromStore));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addRecord = () => {
    setRecords((prev) => [
      ...prev,
      { conceptoCatalogo: "", agrupacion: "", estadoFinanciero: "", unidadNegocio: "" },
    ]);
  };

  const addFromPlantilla = (seleccionados) => {
    setError("");
    setRecords((prev) => {
      // Evita duplicados contra lo ya presente (comparación sin acentos ni mayúsculas).
      const norm = (s) =>
        (s || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const existentes = new Set(
        prev.filter((r) => r.conceptoCatalogo?.trim()).map((r) => norm(r.conceptoCatalogo))
      );
      const nuevos = seleccionados.filter((s) => !existentes.has(norm(s.conceptoCatalogo)));
      return [...prev, ...nuevos];
    });
    setPlantillaOpen(false);
  };

  const updateRecord = (index, field, value) => {
    setRecords((prev) =>
      prev.map((record, i) => {
        if (i === index) {
          const updated = { ...record, [field]: value };
          if (field === "agrupacion") {
            updated.estadoFinanciero = agrupacionesMapeadas[value] || "";
            // La Unidad de Negocio solo aplica a ventas; se limpia si deja de serlo.
            if (value !== "Venta") {
              updated.unidadNegocio = "";
            }
          }
          return updated;
        }
        return record;
      })
    );
  };

  const deleteRecord = (index) => {
    setRecords((prev) => prev.filter((_, i) => i !== index));
  };

  const validateInputs = () => {
    const isValid = records.every(record =>
      record.conceptoCatalogo?.trim() &&
      record.agrupacion?.trim() &&
      record.estadoFinanciero?.trim()
    );

    if (!isValid) {
      setError("Todos los campos son obligatorios.");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (records.length > 0 && !validateInputs()) return;

    setError("");
    const token = sessionStorage.getItem("token");
    setIsLoading(true);

    try {
      const response = await fetch(apiUrl("api/v1/catalogo/conceptos"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(records),
      });

      if (response.ok) {
        dispatch(setConceptos(records));
        navigate("/");
        return;
      }

      if (response.status === 401 || response.status === 403) {
        throw new Error("Unauthorized");
      }

      const errorData = await response.json();
      throw new Error(errorData.message || "Error del servidor");

    } catch (error) {
      console.error("Save error:", error);
      setError(
        error.message === "Unauthorized"
          ? "Sesión expirada. Redirigiendo..."
          : error.message === "Failed to fetch"
          ? "Error de conexión: El servidor no está disponible"
          : error.message
      );

      if (error.message === "Unauthorized" || error.message === "Failed to fetch") {
        sessionStorage.removeItem("token");
        setTimeout(() => navigate("/login"), 2000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{
        mt: 4,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '80vh'
      }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold' }}>
                Catálogo de Conceptos
              </h1>
            </Box>
            <Button
              variant="contained"
              color="success"
              onClick={handleSave}
              disabled={isLoading}
              startIcon={<Save />}
            >
              {isLoading ? "Guardando..." : "Guardar"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Registros de Conceptos"
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<LibraryAddCheck />}
                onClick={() => setPlantillaOpen(true)}
                disabled={isLoading}
                size="small"
              >
                Agregar desde plantilla
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={addRecord}
                disabled={isLoading}
                size="small"
              >
                Agregar Registro
              </Button>
            </Box>
          }
        />
        <Divider />
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Concepto</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Agrupación</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Estado financiero</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Unidad de negocio</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Borrar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={record.conceptoCatalogo}
                        onChange={(e) => updateRecord(index, "conceptoCatalogo", e.target.value)}
                        disabled={isLoading}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <InputLabel>Agrupación</InputLabel>
                        <Select
                          value={record.agrupacion}
                          onChange={(e) => updateRecord(index, "agrupacion", e.target.value)}
                          label="Agrupación"
                          disabled={isLoading}
                        >
                          {agrupaciones.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={record.estadoFinanciero}
                        InputProps={{ readOnly: true }}
                        sx={{
                          '& .MuiInputBase-input': {
                            color: 'black',
                            fontWeight: 500,
                            backgroundColor: 'action.hover'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder={
                          record.agrupacion === "Venta" ? "Ej. Hogar" : "—"
                        }
                        value={record.unidadNegocio || ""}
                        onChange={(e) =>
                          updateRecord(index, "unidadNegocio", e.target.value)
                        }
                        disabled={isLoading || record.agrupacion !== "Venta"}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => deleteRecord(index)}
                        aria-label="Borrar"
                        disabled={isLoading}
                        size="small"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <PlantillaConceptosDialog
        open={plantillaOpen}
        onClose={() => setPlantillaOpen(false)}
        existingRecords={records}
        onAdd={addFromPlantilla}
      />
    </Container>
  );
}

export default ConceptosCatalogo;